import React, { useState, useEffect } from 'react';
import { Menu, X, Phone, Mail } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import logo from '../assets/tolsol-logo.png';

const MENU_ITEMS = [
  { label: 'Inicio', target: 'inicio' },
  { label: 'Nosotros', target: 'nosotros' },
  { label: 'Servicios', target: 'servicios' },
  { label: 'Proyectos', target: 'proyectos' },
  { label: 'Blog', target: 'blog' },
  { label: 'Contacto', target: 'contacto' },
];

const LEFT_ITEMS = ['inicio', 'nosotros', 'servicios'];
const RIGHT_ITEMS = ['proyectos', 'blog', 'contacto'];
const SECTION_ORDER = [...LEFT_ITEMS, ...RIGHT_ITEMS];

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('inicio');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);

      const scrollPosition = window.scrollY + 140;
      let current = SECTION_ORDER[0];

      SECTION_ORDER.forEach((id) => {
        const element = document.getElementById(id);
        if (element && element.offsetTop <= scrollPosition) {
          current = id;
        }
      });

      setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
      setActiveSection(id);
    } else if (location.pathname !== '/') {
      navigate('/', { state: { sectionId: id } });
      setIsMobileMenuOpen(false);
      setActiveSection(id);
    }
  };

  const menuClass = (target) =>
    `text-sm font-semibold tracking-wide transition-colors ${
      activeSection === target ? 'text-[#134b8a]' : 'text-[#4a4a4a] hover:text-[#134b8a]'
    }`;

  const mobileMenuClass = (target) =>
    `text-base font-semibold text-left ${activeSection === target ? 'text-[#134b8a]' : 'text-[#4a4a4a]'}`;

  const leftMenu = MENU_ITEMS.filter((item) => LEFT_ITEMS.includes(item.target));
  const rightMenu = MENU_ITEMS.filter((item) => RIGHT_ITEMS.includes(item.target));

  return (
    <header className="fixed top-0 w-full z-50 text-[#0A192F]" role="banner">
      <div className="bg-[#134b8a] text-white text-xs md:text-sm">
        <div className="container mx-auto flex flex-col sm:flex-row items-center justify-between px-4 py-2 gap-2">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-2">
              <Phone className="w-4 h-4" />
              91 522 76 99
            </span>
            <a href="mailto:info@tolsol.es" className="flex items-center gap-2 hover:text-[#dbe6f3] transition-colors">
              <Mail className="w-4 h-4" />
              info@tolsol.es
            </a>
          </div>
        </div>
      </div>

      <div className={`bg-white transition-all duration-300 ${isScrolled ? 'shadow-lg' : ''}`}>
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between py-4">
            <div className="hidden lg:flex items-center justify-between w-full">
              <nav
                className="flex-1 flex justify-end gap-8 pr-8"
                aria-label="Navegaci\u00f3n principal izquierda"
              >
                {leftMenu.map((item) => (
                  <button
                    key={item.target}
                    onClick={() => scrollToSection(item.target)}
                    className={menuClass(item.target)}
                  >
                    {item.label.toUpperCase()}
                  </button>
                ))}
              </nav>

              <button
                onClick={() => scrollToSection('inicio')}
                className="flex-shrink-0 px-6 flex items-center justify-center focus:outline-none"
                aria-label="Ir al inicio"
              >
                <img src={logo} alt="TOLSOL Toldos y Lonas" className="h-16 w-auto object-contain" loading="lazy" />
              </button>

              <nav
                className="flex-1 flex items-center justify-start gap-8 pl-8"
                aria-label="Navegaci\u00f3n principal derecha"
              >
                {rightMenu.map((item) => (
                  <button
                    key={item.target}
                    onClick={() => scrollToSection(item.target)}
                    className={menuClass(item.target)}
                  >
                    {item.label.toUpperCase()}
                  </button>
                ))}
                <Button
                  onClick={() => scrollToSection('contacto')}
                  className="bg-[#FFD700] text-[#0A192F] hover:bg-[#FFD700]/90 font-semibold whitespace-nowrap"
                >
                  Solicitar presupuesto
                </Button>
              </nav>
            </div>

            <div className="flex lg:hidden items-center justify-between w-full">
              <button
                onClick={() => scrollToSection('inicio')}
                className="flex items-center focus:outline-none"
                aria-label="Ir al inicio"
              >
                <img src={logo} alt="TOLSOL Toldos y Lonas" className="h-12 w-auto object-contain" loading="lazy" />
              </button>
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="text-[#0A192F]"
                aria-label={isMobileMenuOpen ? 'Cerrar men\u00fa' : 'Abrir men\u00fa'}
                aria-expanded={isMobileMenuOpen}
                aria-controls="mobile-navigation"
              >
                {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
              </button>
            </div>
          </div>

          <div id="mobile-navigation" className={`lg:hidden pb-6 ${isMobileMenuOpen ? '' : 'hidden'}`}>
            <div className="flex flex-col gap-4 border-t border-[#E6E6E6] pt-4">
              {MENU_ITEMS.map((item) => (
                <button
                  key={item.target}
                  onClick={() => scrollToSection(item.target)}
                  className={mobileMenuClass(item.target)}
                >
                  {item.label}
                </button>
              ))}
              <Button
                onClick={() => scrollToSection('contacto')}
                className="bg-[#FFD700] text-[#0A192F] hover:bg-[#FFD700]/90 font-semibold"
              >
                Solicitar presupuesto
              </Button>
            </div>
          </div>
        </div>
      </div>

      {isScrolled && (
        <div className="h-2 bg-gradient-to-b from-black/10 via-black/5 to-[#0A192F]/10 pointer-events-none" />
      )}
    </header>
  );
};

export default Header;
